import Select, {
  CSSObjectWithLabel,
  GroupBase,
  OptionProps,
  Props,
  components,
} from "react-select";

import Checkbox from "./Checkbox";
import React from "react";
import { SelectOption } from "../types";
import { useTranslation } from "react-i18next";

export const basicSelectStyles = {
  container: (styles: CSSObjectWithLabel) => ({
    ...styles,
    fontFamily: "var(--font-light)",
    fontWeight: 400,
  }),
  // TODO: Replace with the actual state typing.
  // eslint-disable-next-line
  menu: (styles: CSSObjectWithLabel, state: any) => {
    if (state?.selectProps?.isRelative) {
      styles.position = "relative";
    }
    return {
      ...styles,
      fontFamily: "var(--font-light)",
      fontWeight: 400,
      zIndex: 2,
    };
  },
  // TODO: Replace with the actual state typing.
  // eslint-disable-next-line
  control: (styles: CSSObjectWithLabel) => ({
    ...styles,
    "&:hover": {
      borderColor: "#343a40",
    },
    backgroundColor: "#f8f9fa",
    border: "1px solid #343a40",
    borderRadius: 0,
    color: "var(--color-black)",
    cursor: "pointer",
    fontWeight: 400,
    padding: "1px",
  }),
  indicatorSeparator: (styles: CSSObjectWithLabel) => ({
    ...styles,
    marginLeft: "5px",
  }),
  multiValue: (styles: CSSObjectWithLabel, state: { isDisabled: boolean }) => ({
    ...styles,
    "& div": {
      color: "inherit",
    },
    backgroundColor: "var(--color-light-blue)",
    color: state.isDisabled ? "var(--color-gray-600)" : "initial",
  }),
  // eslint-disable-next-line
  option: (styles: CSSObjectWithLabel, state: any) => {
    const optionHasError = state.options.find(
      (option: { value: string; label: string; hasError?: boolean }) => option.value === state.value
    )?.hasError;

    return {
      ...styles,
      "&:active": {
        backgroundColor: "var(--color-primary)",
        color: "var(--color-black)",
      },
      "&:after": {
        alignSelf: "center",
        backgroundColor: optionHasError && "var(--color-error)",
        backgroundSize: "100% 100%",
        content: '""',

        display: "block",
        height: "18px",
        maskImage:
          optionHasError &&
          `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M 16 3.21875 L 15.125 4.71875 L 3.125 25.5 L 2.28125 27 L 29.71875 27 L 28.875 25.5 L 16.875 4.71875 Z M 16 7.21875 L 26.25 25 L 5.75 25 Z M 15 14 L 15 20 L 17 20 L 17 14 Z M 15 21 L 15 23 L 17 23 L 17 21 Z'/%3E%3C/svg%3E")`,
        width: "16px",
      },
      "&:first-of-type": {
        borderBottom: "0",
      },
      "&:hover": {
        backgroundColor: "var(--color-gray-200)",
      },
      backgroundColor: state.isFocused
        ? "var(--color-gray-200)"
        : optionHasError
        ? "var(--color-primary-light)"
        : "var(--color-white)",
      border: optionHasError && "1px solid var(--color-primary)",
      color: optionHasError ? "var(--color-error)" : "var(--color-black)",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      paddingBottom: "1em",
      paddingTop: "1em",
    };
  },
  placeholder: (styles: CSSObjectWithLabel, state: { isDisabled: boolean }) => ({
    ...styles,
    color: state.isDisabled ? "hsl(0, 0%, 60%)" : "var(--color-black)",
    fontWeight: 400,
  }),
  // TODO: Replace with the actual state typing.
  // eslint-disable-next-line
  dropdownIndicator: (styles: CSSObjectWithLabel, state: any) => {
    const hasError = !!state.options.find(
      (option: { value: string; label: string; hasError?: boolean }) => option.hasError
    );

    return {
      ...styles,
      backgroundColor: hasError ? "var(--color-primary-light)" : "",
      border: hasError ? "1px solid var(--color-error)" : "",
      color: hasError ? "var(--color-error)" : "",
      height: "100%",
      marginRight: "-2px",
    };
  },
};

export const CheckboxOption = <
  IsMulti extends boolean = false,
  Group extends GroupBase<SelectOption> = GroupBase<SelectOption>
>(
  props: OptionProps<SelectOption, IsMulti, Group>
) => {
  // Changing of the selected state is handled by Option component so Checkbox is used
  // only for visualisation. However, onChange handler is needed when Checkbox is used
  // as controlled component so empty handler function is provided
  const onChangeHandler = () => {}; // eslint-disable-line

  return (
    <components.Option {...props}>
      <Checkbox
        id={props.label}
        name={props.label}
        value={props.data.value}
        style={{
          minHeight: "0",
          pointerEvents: "none",
        }}
        checked={props.isSelected}
        onChange={onChangeHandler}
        label={<>{props.label}</>}
      />
    </components.Option>
  );
};

export const DatahubSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> & { isRelative?: boolean }
) => {
  const { t } = useTranslation();

  return (
    <Select
      styles={basicSelectStyles}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
          ...theme.colors,
          danger: "#1f2123",
          dangerLight: "#e0eefd",
          primary: "#1f2123",
          primary25: "#e0eefd",
        },
      })}
      placeholder={t("select.placeholder")}
      {...props}
    />
  );
};
